import { makeHomefront, placeStructure, subIndex } from '../rules/subplotGrid'
import { shapeFootprint } from '../rules/homefrontMaker'
import { addBrood, matureBroodAt } from '../rules/subplotBases'
import { addRocket } from '../rules/subplotRockets'
import { withinDistance, hexDistance } from '../rules/hexGrid'
import { subCenter, hexPoints } from '../utils/hexGeometry'
import { R, LAND, HAIRLINE, ringPoints, PEEK_RING_COLORS } from './v2/hex/geometry'
import HexDefs from './v2/hex/HexDefs'
import { territoryMarks, strokeMarks, baseBarMarks, stampMarks } from './v2/hex/marks'
import ArmyBlock from './v2/ArmyBlock'
import KillBar from './v2/KillBar'
import DropZoneMarker from './v2/DropZoneMarker'

// ─── RulesLegend ──────────────────────────────────────────────────────────────
// The board legend, drawn with the game's own pieces: every Hive sample is a
// few cells of a real homefront put through HexBoard's own mark functions
// (territory, structure strokes, brood bars, clue stamps), and the drones,
// scoreboard and drop zone are the components the board renders. Nothing here
// is a lookalike, so the legend can't drift from the board.

// A patch of one side's Hive: the `focus` cells plus a ring of plain land
// around them, cropped to fit. `extra(cell)` may add elements per cell.
function HexSnippet({ side, hf, focus, ctx = {}, extra = null, label, pad = 1 }) {
    const shown = new Set()
    for (const s of focus) for (const n of withinDistance(s, pad)) shown.add(n)
    const cells = [...shown].map(s => {
        const { x, y } = subCenter(side, s, R)
        return { s, cx: x, cy: y, pts: hexPoints(x, y, R) }
    })
    const xs = cells.flatMap(c => [c.cx - R, c.cx + R])
    const ys = cells.flatMap(c => [c.cy - R, c.cy + R])
    const [x0, y0] = [Math.min(...xs), Math.min(...ys)]
    const [w, h] = [Math.max(...xs) - x0, Math.max(...ys) - y0]
    const pass = (fn) => cells.map(c => {
        const els = fn(side, hf, c.s, hf.subplots[c.s], c.pts, c.cx, c.cy, ctx)
        return els.length ? <g key={c.s}>{els}</g> : null
    })
    return (
        <svg className="rm__hex-snippet" viewBox={`${x0} ${y0} ${w} ${h}`} role="img" aria-label={label}>
            <HexDefs landClipCells={{ opp: [], self: [] }} />
            <g>{cells.map(c => <polygon key={c.s} points={c.pts} fill={LAND[side].fill} stroke={HAIRLINE} strokeWidth="0.5" />)}</g>
            <g>{pass(territoryMarks)}</g>
            <g>{pass(strokeMarks)}</g>
            <g>{pass(baseBarMarks)}</g>
            <g>{pass(stampMarks)}</g>
            {extra && <g>{cells.map(c => extra(c))}</g>}
        </svg>
    )
}

// ── The sample Hives (built once) ─────────────────────────────────────────────
const at = (sc, sr) => subIndex(sc, sr)
const destroy = (hf, s) => { hf.subplots[s].destroyed = true }
const stamp = (hf, s, letter, distance) => Object.assign(hf.subplots[s], { distance, distanceLetter: letter })

function sample(build) {
    const hf = makeHomefront(() => null)
    const focus = build(hf)
    return { hf, focus }
}

const SAMPLES = {
    structure: sample(hf => { placeStructure(hf, 's', shapeFootprint('vee', at(10, 7))); return shapeFootprint('vee', at(10, 7)) }),
    damaged: sample(hf => {
        const fp = shapeFootprint('column', at(10, 7))
        placeStructure(hf, 's', fp); destroy(hf, fp[0])
        return fp
    }),
    rocket: sample(hf => {
        const fp = shapeFootprint('rise', at(10, 7))
        placeStructure(hf, 's', fp); addRocket(hf, 's', fp[1])
        return fp
    }),
    plane: sample(hf => {
        const fp = shapeFootprint('single', at(10, 7))
        placeStructure(hf, 's', fp)
        return fp
    }),
    clue: sample(hf => {
        const aim = at(10, 7)
        const blast = withinDistance(aim, 1)
        blast.forEach(s => destroy(hf, s))
        stamp(hf, aim, 'A', 3)
        return [aim]
    }),
    hit: sample(hf => {
        const fp = shapeFootprint('fall', at(10, 7))
        placeStructure(hf, 's', fp); destroy(hf, fp[0]); destroy(hf, fp[1])
        return fp
    }),
    killed: sample(hf => {
        const fp = shapeFootprint('column', at(10, 7))
        placeStructure(hf, 's', fp); fp.forEach(s => destroy(hf, s))
        return fp
    }),
    broodCon: sample(hf => { addBrood(hf, 3, at(10, 7), 1); return [at(9, 7), at(10, 7), at(11, 7)] }),
    brood: sample(hf => {
        addBrood(hf, 3, at(10, 7), 1); matureBroodAt(hf, 3, at(10, 7))
        return [at(9, 7), at(10, 7), at(11, 7)]
    }),
    ring: sample(hf => {
        const aim = at(10, 7)
        destroy(hf, aim); stamp(hf, aim, 'A', 2)
        return [aim]
    }),
}

// The eye's ring, as HexBoard draws it while the eye is held: every cell at
// the clue's distance, in its letter's hue.
function ringExtra(aim, distance) {
    const color = PEEK_RING_COLORS[0]
    return (c) => hexDistance(c.s, aim) !== distance ? null : (
        <g key={`ring-${c.s}`}>
            <polygon points={c.pts} fill={color} fillOpacity={0.22} />
            <polygon points={ringPoints(c.cx, c.cy, 2)} fill="none" stroke={color} strokeOpacity="0.75" strokeWidth="2" />
        </g>
    )
}

function Drone(props) {
    return <span className="rm__drone"><ArmyBlock gradientId={`rm_${props.kind}_${props.side ?? 'p'}_${props.attacking ? 'a' : ''}${props.delivered ? 'd' : ''}`} {...props} /></span>
}

function EyeIcon() {
    return (
        <svg className="rm__eye" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M2 12c2.4-4.4 5.9-6.6 10-6.6S19.6 7.6 22 12c-2.4 4.4-5.9 6.6-10 6.6S4.4 16.4 2 12z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    )
}

function Row({ icons, children }) {
    return (
        <li className="rm__legend-row">
            <span className="rm__legend-icons">{icons}</span>
            <span className="rm__legend-label">{children}</span>
        </li>
    )
}

export default function RulesLegend() {
    const S = SAMPLES
    return (
        <div className="rm__legend">
            <p className="rm__legend-heading">Drones</p>
            <ul className="rm__legend-list">
                <Row icons={<Drone kind="green" />}>New: deployed this round, still on the ground. It defends straight away, but its corridor can’t attack this round.</Row>
                <Row icons={<Drone kind="vet" />}>Veteran: started the round in its corridor. It can move or attack.</Row>
                <Row icons={<Drone kind="moved" movedFromSide="left" />}>Moved in this round: the striped edge is the side it came from. It fights, but can’t move again or help build a brood.</Row>
                <Row icons={<Drone kind="vet" attacking />}>Attacking this round.</Row>
                <Row icons={<Drone kind="green" delivered />}>Airdropped by one of your broods.</Row>
                <Row icons={<><Drone side="opp" kind="vet" /><Drone side="opp" kind="green" /></>}>Your opponent’s drones: red, or yellow when just deployed. You see their drones, never their orders.</Row>
            </ul>

            <p className="rm__legend-heading">Your Hive</p>
            <ul className="rm__legend-list">
                <Row icons={<HexSnippet side="self" {...S.structure} label="Your structure" />}>Your structure. You have 7.</Row>
                <Row icons={<HexSnippet side="self" {...S.damaged} label="Your damaged structure" />}>Damaged: some hexes destroyed. It still stands, and still earns yield.</Row>
                <Row icons={<HexSnippet side="self" {...S.rocket} label="A rocket on your structure" />}>A rocket stationed on a structure.</Row>
                <Row icons={<HexSnippet side="self" {...S.plane} ctx={{ pendingDroneCells: S.plane.focus }} label="A recon plane ready to launch" />}>A recon plane, launching from this structure when you submit.</Row>
            </ul>

            <p className="rm__legend-heading">Their Hive</p>
            <ul className="rm__legend-list">
                <Row icons={<HexSnippet side="opp" {...S.clue} label="A strike's crater and clue" />}>A strike’s crater, with its clue on the aim hex: structure A is 3 hexes from here.</Row>
                <Row icons={<HexSnippet side="opp" {...S.hit} label="A damaged enemy structure" />}>An enemy structure you’ve hit: its destroyed hexes are crossed out, and the hexes still standing glow orange.</Row>
                <Row icons={<HexSnippet side="opp" {...S.killed} label="A destroyed enemy structure" />}>Destroyed: every hex crossed out. One of their scoreboard icons is crossed out too.</Row>
            </ul>

            <p className="rm__legend-heading">Broods</p>
            <ul className="rm__legend-list">
                <Row icons={<HexSnippet side="self" {...S.broodCon} ctx={{ baseProgress: { [at(10, 7)]: 1 } }} label="A brood under construction" />}>Under construction, with its progress bar. Worth 0, and one destroyed hex destroys it.</Row>
                <Row icons={<HexSnippet side="self" {...S.brood} label="A mature brood with its egg" />}>Mature: a drone on each end (worth 2), and the egg on the nest.</Row>
                <Row icons={<span className="rm__mini-unit"><DropZoneMarker size={22} color="#f0e040" /></span>}>A drop zone: where the brood’s egg lands as a new drone, after the battles.</Row>
            </ul>

            <p className="rm__legend-heading">Clues and the score</p>
            <ul className="rm__legend-list">
                <Row icons={<HexSnippet side="opp" {...S.ring} pad={2} extra={ringExtra(S.ring.focus[0], 2)} label="A clue's ring while the eye is held" />}>
                    Hold the eye <EyeIcon /> in the header: drones and corridors hide, and every clue draws its ring, on both Hives. The structure is somewhere on the ring.
                </Row>
                <Row icons={<span className="rm__killbar"><KillBar lost={1} side="opp" silent /></span>}>The scoreboard: one icon per structure a side can lose. Cross out all 3 of theirs to win.</Row>
            </ul>
        </div>
    )
}
